/**
 * Session changes from elsewhere, for `IdentityApi.subscribe`: another tab signed out or switched organisation (a
 * BroadcastChannel message), or this tab came back into view (re-check, since a session may have ended meanwhile).
 * The session store re-reads `/me` when told.
 */

/** Shared by every tab of the app. */
export const SESSION_CHANNEL = "orthos.identity.session";
const CHANGED = "changed";

export interface SessionSignals {
  subscribe(listener: () => void): () => void;
  /** Tells other tabs this tab's session changed. */
  announce(): void;
}

type Listening = Pick<EventTarget, "addEventListener">;

export interface SignalSources {
  /** Messages between tabs; undefined where BroadcastChannel is missing. */
  channel: (Listening & Pick<BroadcastChannel, "postMessage">) | undefined;
  /** Fires "focus" and "online" (the window). */
  view: Listening | undefined;
}

export function createSignals({ channel, view }: SignalSources): SessionSignals {
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  channel?.addEventListener("message", notify);
  view?.addEventListener("focus", notify);
  view?.addEventListener("online", notify);
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    announce() {
      channel?.postMessage(CHANGED);
    },
  };
}

/** The browser's sources: a channel named SESSION_CHANNEL and the window. */
export function browserSignals(): SessionSignals {
  const channel =
    typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel(SESSION_CHANNEL);
  const view = typeof window === "undefined" ? undefined : window;
  return createSignals({ channel, view });
}
